import { useEffect, useState } from 'react';
import AccordionItemHandler from 'components/forms/new/AccordionItemHandler';
import TrialExpiryDatePicker from '../formFields/TrialExpiryDatePicker';
import { useSelector } from 'react-redux';
import ConvertFromTrial from '../formFields/ConvertFromTrial';
import { FORM_FIELD_PATHS } from '../../constants';
import { useCompanyForm } from '../../hooks/useCompanyForm';
import useConvertFromTrial from '../../queries/useConvertFromTrial';
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Button,
    Text,
    Alert,
    AlertIcon,
} from 'components/v4';
import { Box, Spinner, useDisclosure } from '@chakra-ui/react';
import { toast } from 'react-toastify';

const EntityTrialSection = ({
    darkMode,
    errors,
    touched,
    resetToMainTable,
}) => {
    const { formData } = useCompanyForm();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [conversionError, setConversionError] = useState(null);
    const { currentScope } = useSelector((state) => {
        return { ...state.login };
    });

    // Use the mutation hook
    const {
        mutate: convertFromTrial,
        isLoading: isConverting,
        isSuccess,
    } = useConvertFromTrial(formData?.id, currentScope);

    useEffect(() => {
        if (isSuccess && resetToMainTable) {
            toast.success('Company successfully converted from trial.');
            resetToMainTable();
        }
    }, [isSuccess, resetToMainTable]);

    const onTrialConvertHandler = () => {
        onOpen();
        setConversionError(null);
    };

    const handleConfirmConvert = () => {
        setConversionError(null);
        convertFromTrial();
    };

    const companyName = formData?.name || 'this company';

    return (
        <>
            {formData?.[FORM_FIELD_PATHS.IS_TRIAL] ? (
                <AccordionItemHandler label="Trial Status">
                    <TrialExpiryDatePicker
                        darkMode={darkMode}
                        errors={errors}
                        touched={touched}
                    />
                    <ConvertFromTrial
                        darkMode={darkMode}
                        onConvert={onTrialConvertHandler}
                    />
                    <Modal isOpen={isOpen} onClose={onClose} isCentered>
                        <ModalOverlay />
                        <ModalContent minW="400px">
                            <ModalHeader>
                                Convert Company : {companyName}
                            </ModalHeader>
                            <ModalBody>
                                {!isSuccess && !isConverting && (
                                    <Alert
                                        status="error"
                                        borderRadius="md"
                                        alignItems="flex-start"
                                        py={4}
                                        px={4}>
                                        <AlertIcon mt={1} />
                                        <Box>
                                            <Text fontWeight="bold" mb={2}>
                                                Are you sure you want to convert{' '}
                                                {companyName} from trial?
                                            </Text>
                                            <Text mb={1}>This will:</Text>
                                            <ul style={{ marginLeft: 18 }}>
                                                <li>
                                                    Set the current invoice to{' '}
                                                    <b>
                                                        &#39;ready to post&#39;
                                                    </b>
                                                </li>
                                                <li>
                                                    Remove and de-provision
                                                    trial numbers
                                                </li>
                                                <li>
                                                    Charge setup fees based on
                                                    the currently assigned plan
                                                </li>
                                            </ul>
                                        </Box>
                                    </Alert>
                                )}
                                {isConverting && (
                                    <Box textAlign="center" mt={4}>
                                        <Spinner />
                                        <Text mt={2}>Converting...</Text>
                                    </Box>
                                )}
                                {conversionError && (
                                    <Text color="red.400" mt={2}>
                                        {conversionError}
                                    </Text>
                                )}
                            </ModalBody>
                            <ModalFooter gap={3}>
                                <Button
                                    variant="outline"
                                    minW={90}
                                    bg={darkMode ? 'white' : null}
                                    onClick={onClose}
                                    disabled={isConverting}>
                                    Close
                                </Button>
                                <Button
                                    minW={90}
                                    onClick={handleConfirmConvert}
                                    isLoading={isConverting}
                                    disabled={isConverting}>
                                    Convert
                                </Button>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>
                </AccordionItemHandler>
            ) : (
                <div>This entity is not on a trial.</div>
            )}
        </>
    );
};

export default EntityTrialSection;
